import axios from "axios";
import { API_BACKEND_URL, API_SONICOLA_URL } from "../apiUrls";
import {
  getPendingRedeemContext,
  getRedirectLoginPath,
  getStoredRadioInfo,
  getStoredUser,
} from "./storage";

const radioByNameseoCache = new Map();
const radioByIdCache = new Map();

const asNumber = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const sanitizeString = (value) => {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
};

const toRadioCandidate = (radio = null) => {
  if (!radio || typeof radio !== "object") return null;

  return {
    radioid: asNumber(radio.radioid ?? radio.id),
    radioname: sanitizeString(radio.radioname ?? radio.name),
    nameseo: sanitizeString(radio.nameseo),
  };
};

const toUserCandidate = (user = null) => {
  if (!user || typeof user !== "object") return {};

  return {
    userid: asNumber(user.userid ?? user.id),
    email: sanitizeString(user.email),
  };
};

const mergeUserContext = (...sources) => {
  return sources.reduce(
    (acc, source) => {
      if (!source) return acc;

      const hasEmailConflict =
        !!acc.email && !!source.email && acc.email !== source.email;
      const hasUserConflict =
        !!acc.userid && !!source.userid && acc.userid !== source.userid;

      if (hasEmailConflict || hasUserConflict) {
        return acc;
      }

      return {
        userid: acc.userid ?? source?.userid ?? null,
        email: acc.email ?? source?.email ?? null,
      };
    },
    { userid: null, email: null }
  );
};

export const extractNameseoFromPath = (path) => {
  const cleanedPath = sanitizeString(path);
  if (!cleanedPath) return null;

  const [pathname] = cleanedPath.split("?");
  const match = pathname.match(/^\/sonicbox\/([^/]+)/);
  return match ? match[1] : null;
};

const getRedirectLoginCandidate = () => {
  const redirectPath = getRedirectLoginPath();
  const nameseo = extractNameseoFromPath(redirectPath);
  return nameseo ? { nameseo } : null;
};

const getPendingRedeemCandidate = () => {
  const pendingContext = getPendingRedeemContext();
  if (!pendingContext) return null;

  return toRadioCandidate(pendingContext.radioHint);
};

const getLastVisitedRadioCandidate = () => {
  const storedRadioInfo = getStoredRadioInfo();
  return toRadioCandidate(storedRadioInfo?.lastradiovisited);
};

const parseRadioLookupResponse = (responseData, fallback = {}) => {
  const data = responseData?.data ?? responseData ?? {};

  return {
    radioid: asNumber(data.radioid ?? fallback.radioid),
    radioname: sanitizeString(data.name ?? data.radioname ?? fallback.radioname),
    nameseo: sanitizeString(data.nameseo ?? fallback.nameseo),
  };
};

export const lookupRadioByNameseo = async (nameseo) => {
  const normalizedNameseo = sanitizeString(nameseo);
  if (!normalizedNameseo) return null;

  if (radioByNameseoCache.has(normalizedNameseo)) {
    return radioByNameseoCache.get(normalizedNameseo);
  }

  const response = await axios.post(`${API_BACKEND_URL}getradionameseo`, {
    nameseo: normalizedNameseo,
  });

  const parsedRadio = parseRadioLookupResponse(response.data, {
    nameseo: normalizedNameseo,
  });

  if (parsedRadio.radioid && parsedRadio.radioname) {
    radioByNameseoCache.set(normalizedNameseo, parsedRadio);
    radioByIdCache.set(parsedRadio.radioid, parsedRadio);
    return parsedRadio;
  }

  return null;
};

export const lookupRadioById = async (radioid) => {
  const normalizedRadioId = asNumber(radioid);
  if (!normalizedRadioId) return null;

  if (radioByIdCache.has(normalizedRadioId)) {
    return radioByIdCache.get(normalizedRadioId);
  }

  const response = await axios.post(`${API_SONICOLA_URL}singlecola`, {
    radioid: normalizedRadioId,
  });

  const nestedData =
    response?.data?.data?.data ??
    response?.data?.data ??
    response?.data ??
    {};

  const parsedRadio = parseRadioLookupResponse(nestedData, {
    radioid: normalizedRadioId,
  });

  if (parsedRadio.radioid && parsedRadio.radioname) {
    radioByIdCache.set(normalizedRadioId, parsedRadio);
    if (parsedRadio.nameseo) {
      radioByNameseoCache.set(parsedRadio.nameseo, parsedRadio);
    }
    return parsedRadio;
  }

  return null;
};

const resolveCandidate = async (candidate) => {
  const normalizedCandidate = toRadioCandidate(candidate);
  if (!normalizedCandidate) return null;

  if (
    normalizedCandidate.radioid &&
    normalizedCandidate.radioname &&
    normalizedCandidate.nameseo
  ) {
    return normalizedCandidate;
  }

  if (normalizedCandidate.nameseo) {
    const lookedUpByNameseo = await lookupRadioByNameseo(
      normalizedCandidate.nameseo
    );
    if (lookedUpByNameseo?.radioid && lookedUpByNameseo?.radioname) {
      return {
        ...lookedUpByNameseo,
        nameseo: lookedUpByNameseo.nameseo ?? normalizedCandidate.nameseo,
      };
    }
  }

  if (normalizedCandidate.radioid) {
    const lookedUpById = await lookupRadioById(normalizedCandidate.radioid);
    if (lookedUpById?.radioid && lookedUpById?.radioname) {
      return {
        ...lookedUpById,
        nameseo: lookedUpById.nameseo ?? normalizedCandidate.nameseo,
      };
    }
  }

  return null;
};

export const resolveRadioContext = async ({
  radio = null,
  resolutionMode = "default",
} = {}) => {
  const candidates = [];
  const explicitCandidate = toRadioCandidate(radio);

  if (explicitCandidate) {
    candidates.push(explicitCandidate);
  }

  if (resolutionMode === "login") {
    candidates.push(getRedirectLoginCandidate());
    candidates.push(getPendingRedeemCandidate());
    candidates.push(getLastVisitedRadioCandidate());
  } else {
    candidates.push(getPendingRedeemCandidate());
    candidates.push(getLastVisitedRadioCandidate());
    candidates.push(getRedirectLoginCandidate());
  }

  for (const candidate of candidates) {
    if (!candidate) continue;

    try {
      const resolvedCandidate = await resolveCandidate(candidate);
      if (resolvedCandidate?.radioid && resolvedCandidate?.radioname) {
        return resolvedCandidate;
      }
    } catch (error) {
      console.log("eventEmitter resolveRadioContext error", error);
    }
  }

  return null;
};

export const resolveEventContext = async ({
  user = null,
  radio = null,
  resolutionMode = "default",
} = {}) => {
  const userContext = mergeUserContext(
    toUserCandidate(user),
    toUserCandidate(getStoredUser())
  );
  const radioContext = await resolveRadioContext({ radio, resolutionMode });

  return {
    complete:
      !!userContext.userid &&
      !!userContext.email &&
      !!radioContext?.radioid &&
      !!radioContext?.radioname,
    userContext,
    radioContext,
  };
};
